/**
 * Charm Builder — Cart
 * Adds base + charms as nested cart lines using Shopify's native parent_id.
 * Single POST to /cart/add.js — base chain first, each charm references base
 * via parent_id so Shopify creates a true parent-child relationship throughout
 * cart, checkout, order confirmation, and admin order details.
 *
 * Docs: https://shopify.dev/docs/apps/build/product-merchandising/nested-cart-lines
 *
 * No cart:update event is dispatched — we redirect to /cart directly so the
 * cart drawer is never opened.
 *
 * Also stamps the Shopify cart Note (cart.note) with a fulfillment-facing
 * packing note — see _noteCartAsCharmBuilder().
 */

window.CharmBuilderCart = (() => {
  /**
   * Build the items array for /cart/add.js.
   * State keeps one charm record per canvas unit (each with quantity: 1) so
   * customers can drag them independently. For the cart we aggregate records
   * by (variantId, position) so each unique variant-at-a-position becomes
   * one line with the correct qty — see the position grouping below for why
   * position is part of the key.
   */
  function _buildItems(state) {
    if (!state.base) return [];

    const items = [];

    // Base chain — parent item, no parent_id. Tagged so the cart template can
    // show a bundle heading and a future discount function can detect it.
    items.push({
      id: state.base.variantId,
      quantity: 1,
      properties: {
        _charmbuilderitem: "base",
      },
    });

    // Aggregate charm records by (variantId, position) → total quantity per
    // group. Position is part of the grouping key, not just carried along,
    // because two instances of the SAME charm variant placed at DIFFERENT
    // spots on the necklace must stay as separate cart lines — each needs
    // its own accurate _position_x/_position_y. Only truly-identical
    // variant+position instances combine into one line with qty > 1.
    // x/y are rounded to whole percent to avoid floating-point drag noise
    // splitting what's really the same spot into separate lines.
    const charmGroups = new Map(); // key -> { variantId, x, y, qty }
    state.charms.forEach(charm => {
      const x = Math.round(charm.x || 0);
      const y = Math.round(charm.y || 0);
      const key = `${charm.variantId}|${x}|${y}`;
      const existing = charmGroups.get(key);
      if (existing) {
        existing.qty += charm.quantity || 1;
      } else {
        charmGroups.set(key, { variantId: charm.variantId, x, y, qty: charm.quantity || 1 });
      }
    });

    // Each variant-at-a-position group becomes a child line nested under the
    // base chain. parent_id references the variant ID of the parent item in
    // this same request. Tagged the same way as the base (see above) so a
    // future discount function can identify and count charm lines
    // specifically. "Part of Custom Necklace Set" is deliberately NOT
    // underscore-prefixed — it's meant to stay visible on the customer-facing
    // cart/order (matches the "Custom Necklace Set" bundle heading), while
    // still being a stable, parseable property for ShipStation to key off
    // during fulfillment. Value is the base product's title (not just "Yes")
    // so that if a customer has multiple charm builder sets in one cart, the
    // warehouse team can tell which charm belongs to which base/set.
    // _position_x/_position_y are the charm's placement as a whole-number
    // percentage of .charm-builder__drop-zone (#cb-canvas-drop-zone) — the
    // exact same element GSAP uses as drag bounds in charm-builder-drag.js,
    // NOT the page body or any other ancestor — so they match what the
    // customer actually saw on the canvas.
    charmGroups.forEach(({ variantId, x, y, qty }) => {
      items.push({
        id: variantId,
        quantity: qty,
        parent_id: state.base.variantId,
        properties: {
          _charmbuilderitem: "charm",
          "Part of Custom Necklace Set": state.base.title,
          _position_x: String(x),
          _position_y: String(y),
        },
      });
    });

    return items;
  }

  /**
   * Stamps Shopify's cart Note (cart.note) with a fulfillment-facing marker
   * so the team can tell at a glance this order includes a Charm Builder
   * set. Appends to whatever's already in the note (never overwrites a
   * customer's own instructions) and is idempotent — building a second set
   * in the same cart won't duplicate the line.
   * Best-effort: failures here never block the add-to-cart flow.
   */
  const CART_NOTE_TEXT =
    "This order contains a custom Charm Builder necklace set — please pack all charm set items together.";

  async function _noteCartAsCharmBuilder() {
    try {
      const cartRes = await fetch(window.Shopify.routes.root + "cart.js");
      const cart = await cartRes.json();
      const existing = (cart.note || "").trim();
      if (existing.includes(CART_NOTE_TEXT)) return;

      const note = existing ? `${existing}\n${CART_NOTE_TEXT}` : CART_NOTE_TEXT;
      await fetch(window.Shopify.routes.root + "cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note }),
      });
    } catch (_) {
      // Non-critical — the cart add itself already succeeded.
    }
  }

  /**
   * Dispatches 'charm-builder:cart-added' after a successful add, before the
   * redirect, so listeners (awe-charm-builder-klaviyo.js) can finish network
   * calls the page unload would otherwise cut off. Listeners pass promises to
   * detail.waitUntil(); we wait for them, capped at ADDED_HOOK_TIMEOUT_MS so
   * a slow third party can never hold the customer on the builder.
   */
  const ADDED_HOOK_TIMEOUT_MS = 1500;

  async function _runAddedHooks(state) {
    const pending = [];
    document.dispatchEvent(
      new CustomEvent("charm-builder:cart-added", {
        detail: { state, waitUntil: promise => pending.push(promise) },
      })
    );
    if (!pending.length) return;
    await Promise.race([
      Promise.allSettled(pending),
      new Promise(resolve => setTimeout(resolve, ADDED_HOOK_TIMEOUT_MS)),
    ]);
  }

  async function submit() {
    const state = CharmBuilder.state;
    const items = _buildItems(state);
    if (!items.length) return;

    const btn = document.getElementById("cb-progress-cta");
    const errorEl = document.getElementById("cb-cart-error");

    if (btn) {
      btn.disabled = true;
      btn.textContent = "Adding…";
    }
    if (errorEl) errorEl.hidden = true;

    try {
      const res = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ items }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.description || `Cart error ${res.status}`);
      }

      await _noteCartAsCharmBuilder();
      await _runAddedHooks(JSON.parse(JSON.stringify(state)));

      CharmBuilder.reset();
      window.location.href = window.Shopify.routes.root + "cart";
    } catch (err) {
      document.dispatchEvent(new CustomEvent("charm-builder:cart-error", { detail: err }));
      if (btn) {
        btn.disabled = false;
        btn.textContent = "Add to Cart";
      }
    }
  }

  return { submit };
})();
